import React, { useState } from 'react';

import { FormattedMessage } from '../../../util/reactIntl';
import { isTermFormat } from '../../../util/createClass';
import { showsAsTerm } from '../../../util/termSchedule';

import ClassSessionPicker from './ClassSessionPicker';
import { listingTimeZone, termScheduleSummary } from './classDetailData';

import css from './ClassDetail.module.css';

// Session rows shown before the "+ N more sessions" line on a term class.
const MAX_SESSION_ROWS = 6;

const timeZoneMaybe = timeZone => (timeZone ? { timeZone } : {});

/**
 * Single-session schedule: the SAME calendar + time-slot picker the Activity Booking modal renders
 * (one shared component, so the two can never disagree). For a parent who can book, tapping a time
 * slot here opens the booking modal with that day + slot preselected (`onBookSlot`); for
 * provider/owner previews the slots are display-only chips.
 */
const SingleSessionSchedule = ({ listing, monthlyTimeSlots, intl, onBookSlot, onFetchTimeSlots }) => {
  const [selectedDayKey, setSelectedDayKey] = useState(null);

  return (
    <ClassSessionPicker
      listing={listing}
      monthlyTimeSlots={monthlyTimeSlots}
      intl={intl}
      selectedDayKey={selectedDayKey}
      onSelectDay={setSelectedDayKey}
      onSelectSlot={onBookSlot ? (slotKey, dayKey) => onBookSlot(dayKey, slotKey) : null}
      onFetchTimeSlots={onFetchTimeSlots}
      // Class page only: times left, calendar right (client feedback). The modal and other
      // hosts keep the stacked default.
      layout="columns"
    />
  );
};

/**
 * Term schedule: the enrollment window, the one-upfront framing, and the real session list from
 * publicData.termSchedule (each row day + date + time), truncated past MAX_SESSION_ROWS.
 */
const TermSchedule = ({ listing, intl, isOneSession }) => {
  const summary = termScheduleSummary(listing);
  const timeZone = listingTimeZone(listing);

  if (!summary) {
    return (
      <p className={css.scheduleEmpty}>
        <FormattedMessage id="ClassDetailPage.noUpcomingSessions" />
      </p>
    );
  }

  const dateRange = intl.formatMessage(
    { id: 'ClassDetailPage.termWindow' },
    {
      start: intl.formatDate(summary.start, {
        month: 'short',
        day: 'numeric',
        ...timeZoneMaybe(timeZone),
      }),
      end: intl.formatDate(summary.end, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        ...timeZoneMaybe(timeZone),
      }),
    }
  );

  const rows = summary.sessions.slice(0, MAX_SESSION_ROWS);
  const nowMs = Date.now();
  const moreCount = summary.sessionCount - rows.length;

  return (
    <>
      <div className={css.termSummaryRow}>
        <span className={css.termWindowChip}>{dateRange}</span>
        <span className={css.termMeta}>
          <FormattedMessage
            id="ClassDetailPage.termMeta"
            values={{ count: summary.sessionCount }}
          />
        </span>
      </div>
      {/* "Enroll once ... every session" is term framing; a one-session workaround class skips it. */}
      {isOneSession ? null : (
        <p className={css.termEnrollNote}>
          <FormattedMessage id="ClassDetailPage.termEnrollNote" />
        </p>
      )}
      <ul className={css.sessionList}>
        {rows.map(session => (
          <li key={session.start.toISOString()} className={css.sessionRow}>
            <span className={css.sessionDate}>
              {intl.formatDate(session.start, {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                ...timeZoneMaybe(timeZone),
              })}
            </span>
            <span className={css.sessionTime}>
              {intl.formatTime(session.start, timeZoneMaybe(timeZone))}
              {' – '}
              {intl.formatTime(session.end, timeZoneMaybe(timeZone))}
              {/* Past sessions of a running term read as done, not as something still on offer. */}
              {session.end.getTime() <= nowMs ? (
                <span className={css.sessionPast}>
                  {' · '}
                  <FormattedMessage id="ClassDetailPage.sessionPast" />
                </span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      {moreCount > 0 ? (
        <p className={css.sessionMore}>
          <FormattedMessage id="ClassDetailPage.moreSessions" values={{ count: moreCount }} />
        </p>
      ) : null}
    </>
  );
};

/**
 * "Schedule & availability" -- classFormat-aware: a single-session class shows its bookable days and
 * time slots, a term class shows the enrollment window + session list.
 */
const ClassSchedule = props => {
  const { listing, monthlyTimeSlots, intl, onBookSlot, onFetchTimeSlots } = props;
  const publicData = listing.attributes?.publicData;
  const isTerm = isTermFormat(publicData?.classFormat);
  // A one-session term (the old one-off-class workaround) reads as a drop-in.
  const isOneSession = isTerm && !showsAsTerm(publicData);

  return (
    <section className={css.card}>
      <h2 className={css.sectionTitle}>
        <FormattedMessage id="ClassDetailPage.scheduleTitle" />
      </h2>
      <p className={css.sectionSubtitle}>
        <FormattedMessage
          id={
            isOneSession
              ? 'ClassDetailPage.scheduleSubtitleOneSession'
              : isTerm
              ? 'ClassDetailPage.scheduleSubtitleTerm'
              : 'ClassDetailPage.scheduleSubtitle'
          }
        />
      </p>
      {isTerm ? (
        <TermSchedule listing={listing} intl={intl} isOneSession={isOneSession} />
      ) : (
        <SingleSessionSchedule
          listing={listing}
          monthlyTimeSlots={monthlyTimeSlots}
          intl={intl}
          onBookSlot={onBookSlot}
          onFetchTimeSlots={onFetchTimeSlots}
        />
      )}
    </section>
  );
};

export default ClassSchedule;
